import { useEffect, useMemo, useRef, useState } from "react";
import useSWR, { type Key, type RevalidatorOptions, unstable_serialize, useSWRConfig } from "swr";
import useSWRInfinite from "swr/infinite";
import { fetchJson, type HttpError, isAbort } from "@/api/http";
import { fetchActiveMarkets, fetchLargeTrades, fetchLongDatedMarkets, type GammaMarket, type LargeTrade } from "@/api/polymarketApi";
import { buildApiUrl } from "@/api/terminalApi";
import type { AlertsResponse, MarketsResponse } from "@/types/terminal";
import { type AlertsMeta, type FlaggedRow, onlyWallet, type TokenStats, toFlaggedRow } from "./flagged";
import { normalizeMarket } from "./markets";
import { useApiStatus } from "./status";
import { rankMarkets } from "./unusual";

export const ALERTS_PAGE = 100; // the API's MAX_LIMIT

const STATIC_DATA = { revalidateOnFocus: false, dedupingInterval: 60_000 } as const;

// A view handing over to one that wants the same key subscribes only after paint.
const ABORT_GRACE_MS = 250;
// A drawer that fails has a retry button, so it need not keep trying by itself.
const ONE_SHOT_RETRIES = 3;

// Per-key bookkeeping shared by every component using that key: who is listening, the
// signal their requests hang off, and when real data last arrived.
interface Slot {
	subscribers: number;
	ctl: AbortController | null;
	updatedAt: number | null;
	listeners: Set<() => void>;
}

const slots = new Map<string, Slot>();

function slotFor(id: string): Slot {
	let s = slots.get(id);
	if (!s) {
		s = { subscribers: 0, ctl: null, updatedAt: null, listeners: new Set() };
		slots.set(id, s);
	}
	return s;
}

function signalFor(id: string): AbortSignal {
	const s = slotFor(id);
	if (!s.ctl || s.ctl.signal.aborted) s.ctl = new AbortController();
	return s.ctl.signal;
}

function stamp(id: string): void {
	const s = slotFor(id);
	s.updatedAt = Date.now();
	for (const l of s.listeners) l();
}

/** Aborts `id`'s requests once its last component unmounts, and tracks when it last updated. */
function useRequestScope(id: string | null): number | null {
	const [updatedAt, setUpdatedAt] = useState<number | null>(id ? (slots.get(id)?.updatedAt ?? null) : null);
	useEffect(() => {
		if (!id) {
			setUpdatedAt(null);
			return;
		}
		const s = slotFor(id);
		const onStamp = () => setUpdatedAt(s.updatedAt);
		onStamp();
		s.subscribers += 1;
		s.listeners.add(onStamp);
		return () => {
			s.listeners.delete(onStamp);
			s.subscribers -= 1;
			window.setTimeout(() => {
				if (s.subscribers === 0) s.ctl?.abort();
			}, ABORT_GRACE_MS);
		};
	}, [id]);
	return updatedAt;
}

export interface Live<T> {
	data: T | undefined;
	error: unknown;
	isLoading: boolean;
	isValidating: boolean;
	mutate: () => Promise<T | undefined>;
	/** When data last arrived from the source, in this browser. Null until it has. */
	updatedAt: number | null;
}

interface LiveOptions {
	/** Poll this often while visible. Leave out for data fetched once. */
	refreshMs?: number;
	dedupingMs?: number;
}

const retryDelayMs = (attempt: number, refreshMs: number | undefined): number =>
	Math.min(refreshMs ?? 60_000, 2_000 * 2 ** Math.min(attempt, 6));

type Retry = (err: unknown, key: string, config: unknown, revalidate: (o: RevalidatorOptions) => unknown, opts: Required<RevalidatorOptions>) => void;

/** SWR gives up polling while an error is cached, so this retry is what keeps a poll alive. */
const politeRetry =
	(refreshMs?: number): Retry =>
	(err, _key, _config, revalidate, opts) => {
		if (isAbort(err)) return;
		if (!refreshMs && opts.retryCount > ONE_SHOT_RETRIES) return;
		const wait = Math.max((err as HttpError).retryAfterMs ?? 0, retryDelayMs(opts.retryCount, refreshMs));
		window.setTimeout(() => {
			// Hidden: stop here. useCatchUp starts it again when the tab returns.
			if (document.visibilityState === "visible") void revalidate(opts);
		}, wait);
	};

/** Refetches as soon as the tab is visible again, if `isDue` says the data fell behind. */
function useCatchUp(mutate: () => unknown, isDue: () => boolean): void {
	const due = useRef(isDue);
	due.current = isDue;
	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState === "visible" && due.current()) void mutate();
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => document.removeEventListener("visibilitychange", onVisible);
	}, [mutate]);
}

/**
 * SWR with the parts it leaves out: requests abort when nobody is left to read them, a failed
 * poll keeps retrying at a polite rate instead of stopping, the 429 cooldown is honoured, and a
 * tab coming back from hidden catches up at once. SWR already skips polls while hidden.
 */
export function useLive<T>(key: Key, fetcher: (signal: AbortSignal) => Promise<T>, options: LiveOptions = {}): Live<T> {
	const { refreshMs, dedupingMs } = options;
	const { cache } = useSWRConfig();
	const id = key === null ? null : unstable_serialize(key);
	const res = useSWR<T>(
		key,
		async () => {
			const scope = id as string;
			try {
				const value = await fetcher(signalFor(scope));
				stamp(scope);
				return value;
			} catch (e) {
				// Left the view: keep what we had, rather than recording a failure nobody caused.
				// With nothing cached it has to throw, or SWR would hold undefined as the answer.
				const cached = cache.get(scope)?.data as T | undefined;
				if (isAbort(e) && cached !== undefined) return cached;
				throw e;
			}
		},
		{
			refreshInterval: refreshMs ?? 0,
			dedupingInterval: dedupingMs ?? (refreshMs ? refreshMs / 2 : 2_000),
			keepPreviousData: true,
			revalidateOnFocus: false,
			onErrorRetry: politeRetry(refreshMs),
		},
	);
	const updatedAt = useRequestScope(id);
	const { mutate } = res;
	const error = isAbort(res.error) ? undefined : res.error;

	useCatchUp(mutate, () => {
		if (!id) return false;
		if (error) return true;
		return Boolean(refreshMs) && Date.now() - (slots.get(id)?.updatedAt ?? 0) >= (refreshMs as number);
	});

	return { data: res.data, error, isLoading: res.isLoading, isValidating: res.isValidating, mutate, updatedAt };
}

export const MARKETS_REFRESH_MS = 120_000;
export const TRADES_REFRESH_MS = 30_000;
const LONG_DATED_AFTER_MS = 48 * 3_600_000;
const HOUR_MS = 3_600_000;

interface MarketLoad {
	list: GammaMarket[];
	/** One of the two lists failed, so the set and its peer medians are incomplete. */
	partial: boolean;
}

async function loadMarkets(signal: AbortSignal): Promise<MarketLoad> {
	// Whole hours, so repeat requests share a URL.
	const after = Math.floor((Date.now() + LONG_DATED_AFTER_MS) / HOUR_MS) * HOUR_MS;
	const [top, longDated] = await Promise.allSettled([
		fetchActiveMarkets(undefined, signal),
		fetchLongDatedMarkets(new Date(after).toISOString(), undefined, signal),
	]);
	if (top.status === "rejected" && longDated.status === "rejected") throw top.reason;
	if (signal.aborted) throw new DOMException("Aborted", "AbortError");
	const lists = [top, longDated].flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
	const byId = new Map(lists.flat().map((m) => [m.conditionId, m]));
	return { list: [...byId.values()], partial: lists.length < 2 };
}

/** About 600 open markets: the busiest overall, plus the busiest that end after the next 48h. */
export function useActiveMarkets() {
	const res = useLive("pm:markets", loadMarkets, { refreshMs: MARKETS_REFRESH_MS });
	const markets = useMemo(() => (res.data?.list ?? []).map((m) => normalizeMarket(m)), [res.data]);
	return { ...res, markets, partial: res.data?.partial ?? false };
}

export function useRankedMarkets() {
	const res = useActiveMarkets();
	const ranked = useMemo(() => rankMarkets(res.markets, Date.now()), [res.markets]);
	return { ...res, ranked };
}

export function useLargeTrades(minUsd: number): Live<LargeTrade[]> {
	return useLive<LargeTrade[]>(["pm:trades", minUsd], (signal) => fetchLargeTrades({ minUsd }, signal), { refreshMs: TRADES_REFRESH_MS });
}

/** Mean and spread per token for the most active markets, used to place a position in Z terms. */
function useTokenStats() {
	const api = useApiStatus();
	const { data } = useSWR(
		api.state === "up" ? "api:token-stats" : null,
		() => apiJson<MarketsResponse>("/top-liquidity-markets", { limit: 100 }),
		STATIC_DATA,
	);
	return useMemo(() => {
		const map = new Map<string, TokenStats>();
		for (const m of data?.data ?? []) {
			if (m.tokenId) map.set(String(m.tokenId), { mean: m.mean, stdDev: m.stdDev });
		}
		return map;
	}, [data]);
}

type AlertsPage = AlertsResponse & { meta?: AlertsMeta };

export const FLAGS_REFRESH_MS = 60_000;

/** One request to our API, with the same timeout and failure hold as Polymarket's. */
function apiJson<T>(path: string, query?: Record<string, string | number | undefined>, signal?: AbortSignal): Promise<T> {
	return fetchJson<T>(buildApiUrl(path, query), { signal });
}

export interface FlaggedFeed {
	admission?: AlertsMeta["admission"];
	rows: FlaggedRow[];
	total: number | null;
	isLoading: boolean;
	error: unknown;
	updatedAt: number | null;
	loadMore: () => void;
	canLoadMore: boolean;
	loadingMore: boolean;
}

/** Flagged positions, newest first, 100 per page. Waits while the API is down. */
export function useFlaggedFeed(minZ?: number): FlaggedFeed {
	const api = useApiStatus();
	const stats = useTokenStats();
	const feedId = `api:alerts:${minZ ?? ""}`;
	const res = useSWRInfinite<AlertsPage>(
		(index, previous) => {
			if (api.state === "down") return null;
			if (previous && !previous.pagination.hasNext) return null;
			return ["api:alerts", index + 1, minZ ?? null];
		},
		async ([, page]: [string, number]) => {
			const body = await apiJson<AlertsPage>("/alerts", { page, limit: ALERTS_PAGE, min_zscore: minZ }, signalFor(feedId));
			// Polling refetches page 1 only, so that is what "updated" means here.
			if (page === 1) stamp(feedId);
			return body;
		},
		{
			initialSize: 2,
			keepPreviousData: true,
			revalidateOnFocus: false,
			refreshInterval: FLAGS_REFRESH_MS,
			onErrorRetry: politeRetry(FLAGS_REFRESH_MS),
		},
	);
	const updatedAt = useRequestScope(feedId);
	const error = isAbort(res.error) ? undefined : res.error;
	useCatchUp(res.mutate, () => Boolean(error) || Date.now() - (slots.get(feedId)?.updatedAt ?? 0) >= FLAGS_REFRESH_MS);
	const rows = useMemo(() => {
		const all = (res.data ?? []).flatMap((p) => p.data).map((a) => toFlaggedRow(a, stats));
		// New flags push rows from a fresh page 1 into the cached page 2, so some arrive twice.
		return [...new Map(all.map((r) => [r.id, r])).values()];
	}, [res.data, stats]);
	const firstPage = res.data?.[0];
	const lastPage = res.data?.[res.data.length - 1];
	return {
		admission: firstPage?.meta?.admission,
		rows,
		total: firstPage?.pagination.total ?? null,
		isLoading: res.isLoading,
		error,
		updatedAt,
		loadMore: () => void res.setSize((n) => n + 2),
		canLoadMore: Boolean(lastPage?.pagination.hasNext),
		loadingMore: res.isValidating && (res.data?.length ?? 0) < res.size,
	};
}

const WALLET_FLAGS_LIMIT = 100;

/**
 * Flags on one wallet, asked of the server. `exact` says the server applied the filter: only
 * then does an empty answer mean anything beyond the rows it happened to return.
 */
export function useWalletFlags(address: string) {
	const api = useApiStatus();
	const stats = useTokenStats();
	const res = useLive<AlertsPage>(api.state === "down" ? null : ["api:alerts:wallet", address], (signal) =>
		apiJson<AlertsPage>("/alerts", { wallet: address, limit: WALLET_FLAGS_LIMIT }, signal),
	);
	const rows = useMemo(
		() => onlyWallet((res.data?.data ?? []).map((a) => toFlaggedRow(a, stats)), address),
		[res.data, stats, address],
	);
	const meta = res.data?.meta;
	return {
		rows,
		exact: meta?.total !== undefined,
		total: meta?.total ?? null,
		emptyReason: meta?.empty_reason ?? null,
		scanned: res.data?.data.length ?? 0,
		isLoading: res.isLoading,
		error: res.error,
	};
}

export { STATIC_DATA };
